import React, { useState } from "react";
import { Link } from "react-router-dom";
import cateringData from "../data/barbequeen-outdoor-catering.json";
import SEO from "../components/SEO";
import { cateringPageSchema } from "../data/schemas";
import FAQSection from "../components/FAQSection";
import { cateringFaqs } from "../data/faqs";

const pageData = cateringData.page;
const intro = pageData.intro;
const galleryImages = pageData.gallerySection.images;

const CateringPage = () => {
  const [lightboxImage, setLightboxImage] = useState(null);

  return (
    <div className="min-h-screen bg-[#f9f5f0]" style={{ fontFamily: "'Inter', sans-serif" }}>
      <SEO
        title="Barbequeen Outdoor Catering | Wedding & Corporate Events TN"
        metaTitle="Barbequeen Outdoor Catering | Wedding & Corporate Events TN"
        description="Book Barbequeen Outdoor Catering for weddings, corporate events, and parties across Tamil Nadu. Live BBQ counters, custom menus, and over 25 years of trust."
        keywords="Barbequeen outdoor catering, catering services Tamil Nadu, wedding catering Salem, live BBQ catering, corporate event catering, catering services Coimbatore"
        robots="index, follow"
        canonical="https://barbequeen.com/outdoor-catering"
        og={{
          type: "website",
          url: "https://barbequeen.com/outdoor-catering",
          title: "Barbequeen Outdoor Catering | Memorable Catering Services",
          description: "Professional outdoor catering for weddings, receptions, parties, and corporate events across Tamil Nadu. Live BBQ counters & custom menus.",
          image: "https://barbequeen.com/images/catering-banner.jpg",
          siteName: "Barbequeen Restaurant",
          locale: "en_IN"
        }}
        geo={{
          region: "IN-TN",
          placename: "Tamil Nadu, India"
        }}
        schema={cateringPageSchema}
      />

      {/* ── Lightbox Modal ── */}
      {lightboxImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm">
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <div className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col items-center animate-fade-in">
            <img
              src={lightboxImage.full}
              alt={lightboxImage.caption}
              className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl"
              onError={(e) => {
                e.target.src = "https://images.unsplash.com/photo-1555244162-803834f70033?q=80&w=1200&auto=format&fit=crop";
              }}
            />
            <div className="mt-4 text-center">
              <span className="px-2.5 py-0.5 bg-[#9b1c2e] text-white text-[10px] uppercase font-bold tracking-widest rounded-full mb-1.5 inline-block">
                Catering setup
              </span>
              <p className="text-white text-sm font-semibold">{lightboxImage.caption}</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Hero Banner ── */}
      <section className="relative overflow-hidden" style={{ minHeight: "340px" }}>
        <img
          src="https://images.unsplash.com/photo-1555244162-803834f70033?q=80&w=1600&auto=format&fit=crop"
          alt="Outdoor Catering"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(to bottom, rgba(10,0,2,0.72) 0%, rgba(10,0,2,0.82) 100%)" }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8 py-28 text-center">
          <p className="text-xs font-bold tracking-[0.5em] uppercase text-[#c0424e] mb-4">Professional Services</p>
          <h1 className="text-4xl sm:text-6xl font-bold text-white uppercase leading-tight tracking-wide mb-5" style={{ fontFamily: "'Inter', sans-serif" }}>
            {intro.heading}
          </h1>
          <p className="text-gray-300 text-base max-w-xl mx-auto leading-relaxed">
            Delicious food and seamless, top-quality service to make your celebrations absolute highlights.
          </p>
        </div>
      </section>

      {/* ── Intro Details & Video ── */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-16 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Text details */}
          <div className="lg:col-span-7 space-y-6">
            <span className="text-xs font-bold tracking-widest text-[#9b1c2e] uppercase block">
              {intro.subtitle}
            </span>
            <h2 className="text-3xl font-bold text-gray-900 leading-tight" style={{ fontFamily: "'Inter', sans-serif" }}>
              Catering Made Memorable
            </h2>
            <div className="w-20 h-[3px] bg-[#9b1c2e]" />
            
            <div className="space-y-4 text-sm text-gray-600 leading-relaxed font-medium">
              {intro.paragraphs.map((p, idx) => (
                <p key={idx}>{p}</p>
              ))}
            </div>

            <div className="pt-2">
              <Link
                to="/contact-us"
                className="inline-flex items-center justify-center px-8 py-3.5 bg-[#9b1c2e] hover:bg-[#7d1624] text-white text-xs font-bold tracking-widest uppercase rounded-xl transition-all duration-200 shadow-md shadow-[#9b1c2e]/10"
              >
                {intro.cta.text}
              </Link>
            </div>
          </div>

          {/* YouTube Video iframe */}
          {intro.video && (
            <div className="lg:col-span-5">
              <div className="relative rounded-3xl overflow-hidden shadow-xl border-4 border-white bg-white aspect-video animate-fade-in">
                <iframe
                  src={intro.video.url}
                  title={intro.video.title}
                  className="absolute inset-0 w-full h-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          )}

        </div>
      </section>

      {/* ── Gallery Grid ── */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 pb-24">
        
        {/* Gallery Title (Centered like testimonials) */}
        <div className="text-center max-w-xl mx-auto mb-16">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#9b1c2e] mb-2">Event Highlights</p>
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 uppercase leading-snug tracking-wider" style={{ fontFamily: "'Inter', sans-serif" }}>
            Catering Gallery
          </h3>
          <div className="w-12 h-0.5 bg-[#9b1c2e] mx-auto mt-4 mb-2" />
          <span className="text-[11px] text-gray-400 font-sans tracking-wide block">
            {galleryImages.length} Captured setups & presentation moments
          </span>
        </div>

        {/* Photos Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {galleryImages.map((img, idx) => (
            <div
              key={idx}
              onClick={() => setLightboxImage(img)}
              className="group cursor-pointer rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 relative aspect-[4/3]"
            >
              <img
                src={img.thumbnail}
                alt={img.caption}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
                onError={(e) => {
                  e.target.src = "https://images.unsplash.com/photo-1555244162-803834f70033?q=80&w=600&auto=format&fit=crop";
                }}
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <div className="w-10 h-10 rounded-full bg-white/25 backdrop-blur-sm border border-white/40 flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>

      </section>

      <FAQSection faqs={cateringFaqs} />
    </div>
  );
};

export default CateringPage;

